import { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { LogoLoader } from '@/components/ui/logo-loader';

function buildResetPasswordSchema(t: (key: string, opts?: any) => string) {
  return z.object({
    newPassword: z.string({ message: t('pages.resetPassword:validation.newPassword.required') })
      .min(6, t('pages.resetPassword:validation.newPassword.min'))
      .max(50),
    confirmPassword: z.string({ message: t('pages.resetPassword:validation.confirmPassword.required') })
      .min(1, t('pages.resetPassword:validation.confirmPassword.required')),
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: t('pages.resetPassword:validation.confirmPassword.match'),
    path: ['confirmPassword'],
  });
}

type ResetPasswordFormData = z.infer<ReturnType<typeof buildResetPasswordSchema>>;

export default function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const [, setLocation] = useLocation();
  const { t, i18n } = useTranslation(['common', 'pages.resetPassword', 'pages.forgotPassword']);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { toast } = useToast();

  const schema = useMemo(() => buildResetPasswordSchema(t), [t, i18n.language]);
  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token || token.length !== 40) {
        setIsValidToken(false);
        setErrorMessage(t('pages.resetPassword:errors.invalidToken'));
        setIsVerifying(false);
        return;
      }

      try {
        const response = await apiClient.verifyResetToken(token);
        if (response.success) {
          setIsValidToken(true);
        } else {
          setIsValidToken(false);
          const errorMsg = response.message || t('pages.resetPassword:errors.invalidToken');
          
          // Check for specific error types
          if (errorMsg.toLowerCase().includes('expired')) {
            setErrorMessage(t('pages.resetPassword:errors.expiredToken'));
          } else if (errorMsg.toLowerCase().includes('used') || errorMsg.toLowerCase().includes('already')) {
            setErrorMessage(t('pages.resetPassword:errors.usedToken'));
          } else {
            setErrorMessage(errorMsg);
          }
        }
      } catch (error: any) {
        console.error('Token verification error:', error);
        setIsValidToken(false);
        const errorMsg = error?.message || t('pages.resetPassword:errors.invalidToken');
        
        if (errorMsg.toLowerCase().includes('expired')) {
          setErrorMessage(t('pages.resetPassword:errors.expiredToken'));
        } else if (errorMsg.toLowerCase().includes('used') || errorMsg.toLowerCase().includes('already')) {
          setErrorMessage(t('pages.resetPassword:errors.usedToken'));
        } else {
          setErrorMessage(errorMsg);
        }
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token, t]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return;

    setIsLoading(true);
    try {
      const response = await apiClient.resetPassword(token, data.newPassword, i18n.language);
      
      if (response.success) {
        setIsSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          setLocation('/login');
        }, 3000);
      } else {
        toast({
          title: t('pages.resetPassword:errors.generic'),
          description: response.message || t('pages.resetPassword:errors.generic'),
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Password reset error:', error);
      const errorMsg = error?.message || t('pages.resetPassword:errors.generic');
      
      toast({
        title: t('pages.resetPassword:errors.generic'),
        description: errorMsg,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4">
              <LogoLoader size="md" />
              <p className="text-sm text-light">{t('pages.resetPassword:subtitle')}</p>
            </div>
          </CardContent>
        </Card>
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    // Determine the appropriate error title based on the error message
    const getErrorTitle = () => {
      if (!errorMessage) return t('pages.resetPassword:errors.invalidToken');
      const msg = errorMessage.toLowerCase();
      if (msg.includes('expired')) return t('pages.resetPassword:errors.expiredToken');
      if (msg.includes('used') || msg.includes('already')) return t('pages.resetPassword:errors.usedToken');
      return t('pages.resetPassword:errors.invalidToken');
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle className="text-xl font-bold text-dark">{getErrorTitle()}</CardTitle>
            <CardDescription className="mt-2 text-sm text-light">
              {errorMessage || t('pages.resetPassword:errors.invalidToken')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Link href="/forgot-password">
              <Button className="w-full" variant="default">
                {t('pages.resetPassword:title')}
              </Button>
            </Link>
            <Link href="/login">
              <Button className="w-full" variant="outline">
                {t('pages.forgotPassword:backToLogin')}
              </Button>
            </Link>
          </CardContent>
        </Card>
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle className="text-xl font-bold text-dark">{t('pages.resetPassword:successTitle')}</CardTitle>
            <CardDescription className="mt-2 text-sm text-light">
              {t('pages.resetPassword:successMessage')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-center text-sm text-light mb-4">
              {t('pages.resetPassword:redirecting')}
            </p>
            <Link href="/login">
              <Button className="w-full" variant="default">
                {t('pages.forgotPassword:backToLogin')}
              </Button>
            </Link>
          </CardContent>
        </Card>
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-dark text-center">{t('pages.resetPassword:title')}</CardTitle>
          <CardDescription className="text-center text-sm text-light">
            {t('pages.resetPassword:subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              // Manually validate using safeParse to prevent unhandled rejections
              const values = form.getValues();
              const result = schema.safeParse(values);
              
              if (!result.success) {
                // Clear previous errors and set new ones manually
                form.clearErrors();
                for (const issue of result.error.issues) {
                  const field = issue.path?.[0] as keyof ResetPasswordFormData;
                  if (field) {
                    form.setError(field, {
                      type: 'validation',
                      message: issue.message,
                    }, { shouldFocus: false });
                  }
                }
                return;
              }
              
              // If validation passes, call onSubmit
              onSubmit(result.data);
            }} className="space-y-4">
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-medium text-dark mb-1.5 block">{t('pages.resetPassword:newPassword')}</FormLabel>
                    <FormControl>
                      <Input 
                        {...field}
                        type="password"
                        placeholder="••••••••" 
                        disabled={isLoading}
                        autoComplete="new-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-medium text-dark mb-1.5 block">{t('pages.resetPassword:confirmPassword')}</FormLabel>
                    <FormControl>
                      <Input 
                        {...field}
                        type="password"
                        placeholder="••••••••" 
                        disabled={isLoading}
                        autoComplete="new-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <Button 
                type="submit" 
                className="w-full h-10 text-xs font-semibold bg-[var(--primary-blue)] text-white rounded-md shadow-[var(--shadow-subtle)] hover:bg-[#3a7bc8] hover:shadow-[0_4px_12px_rgba(74,144,226,0.2)] transition-all"
                disabled={isLoading}
              >
                {isLoading ? t('pages.resetPassword:submitting') : t('pages.resetPassword:submit')}
              </Button>
              
              <div className="text-center">
                <Link href="/login" className="text-primary-blue hover:text-[#3a7bc8] text-xs font-medium">
                  {t('pages.forgotPassword:backToLogin')}
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
    </div>
  );
}

